import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { ArrowRight, AudioLines, BadgeCheck, ClipboardList, FileAudio, Globe2, ListTodo, Mic, ShieldCheck, Sparkles, Users } from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";

const samples = {
  zh: "张总：这周要把客户演示版本定下来，重点是会议纪要、行动项和风险提醒。李工：后端接口周三能完成，但音频上传还需要确认存储方案。王经理：销售侧已经约了两个客户，下周二和周四各一场演示。张总：先用转写文本版本上线，音频 ASR 放到下一阶段。李工负责接口，王经理负责客户名单，我周五检查演示链路。",
  en: "Alex: We need to lock the customer demo this week, focusing on minutes, action items and risk alerts. Engineer: Backend API can be done by Wednesday, but audio upload still needs storage confirmation. Sales: Two customer demos are scheduled next Tuesday and Thursday. Alex: Launch the transcript-based version first, audio ASR comes next. Engineer owns API, Sales owns customer list, I will review the demo flow on Friday."
};

const copy = {
  zh: {
    brand: "启序听记",
    lang: "EN",
    nav: ["能力", "纪要", "治理"],
    label: "独立案例站点 09 / 语音会议记录与智能纪要",
    title: "一个能把会议转写变成行动项和决策的 AI 语音网站。",
    subtitle: "面向销售、项目管理和内部协作场景，将会议转写文本整理成摘要、决策、待办、风险和追问事项。",
    primary: "生成纪要",
    secondary: "查看能力",
    featuresTitle: "会议智能能力",
    featuresSubtitle: "从语音内容沉淀可执行的团队记录。",
    features: [
      ["转写整理", "清理口语化表达，提取结构化会议内容。"],
      ["决策识别", "识别已经确认的结论，避免会后信息丢失。"],
      ["行动项生成", "提取负责人、截止时间和交付物。"],
      ["风险提醒", "标记阻塞点、依赖项和需要追问的信息。"]
    ],
    deskTitle: "AI 会议纪要工作台",
    deskSubtitle: "后端直连 MiniMax，基于会议转写生成可直接发送的纪要。",
    transcript: "会议转写文本",
    meetingType: "会议类型",
    run: "生成纪要",
    sample: "填入示例",
    waiting: "等待会议转写文本。",
    governanceTitle: "语音数据治理",
    governanceSubtitle: "会议记录产品要关注授权、隐私、留存和误识别复核。",
    panels: [
      ["录音授权", "会议开始前明确录音和 AI 处理范围。"],
      ["人工复核", "关键决策和负责人需要会后确认。"],
      ["安全留存", "敏感会议设置更短留存周期和访问权限。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 9 个真实独立网站"
  },
  en: {
    brand: "Qixu Voice Minutes",
    lang: "中文",
    nav: ["Features", "Minutes", "Govern"],
    label: "Standalone Case Site 09 / Voice Meetings and Smart Minutes",
    title: "An AI voice site that turns meeting transcripts into decisions and actions.",
    subtitle: "For sales, project management and internal collaboration: convert transcripts into summaries, decisions, tasks, risks and follow-ups.",
    primary: "Generate minutes",
    secondary: "View features",
    featuresTitle: "Meeting Intelligence",
    featuresSubtitle: "Turn spoken meetings into actionable team records.",
    features: [
      ["Transcript cleanup", "Clean conversational text and extract structured content."],
      ["Decision detection", "Capture confirmed conclusions before they are lost."],
      ["Action items", "Extract owners, due dates and deliverables."],
      ["Risk alerts", "Flag blockers, dependencies and missing details."]
    ],
    deskTitle: "AI Meeting Minutes Desk",
    deskSubtitle: "The backend calls MiniMax directly to produce shareable minutes from transcripts.",
    transcript: "Transcript",
    meetingType: "Meeting type",
    run: "Generate minutes",
    sample: "Use sample",
    waiting: "Waiting for a meeting transcript.",
    governanceTitle: "Voice Data Governance",
    governanceSubtitle: "Meeting products need consent, privacy, retention and recognition review.",
    panels: [
      ["Recording consent", "Clarify recording and AI processing scope before meetings."],
      ["Human review", "Confirm key decisions and owners after the meeting."],
      ["Secure retention", "Use shorter retention and access control for sensitive meetings."]
    ],
    footer: "Qixu Technology AI case site · ninth real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [transcript, setTranscript] = useState(samples.zh);
  const [meetingType, setMeetingType] = useState("项目例会");
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setTranscript(samples[next]);
    setMeetingType(next === "zh" ? "项目例会" : "Project sync");
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function generate() {
    if (!transcript.trim() || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在生成会议纪要..." : "Generating meeting minutes...");
    setMeta("");
    try {
      const response = await fetch("/api/minutes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transcript, meetingType, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，无法生成纪要。" : "Network error. Minutes unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>听</span><strong>{t.brand}</strong></a>
        <nav><a href="#features">{t.nav[0]}</a><a href="#desk">{t.nav[1]}</a><a href="#governance">{t.nav[2]}</a></nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#desk">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#features">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><Mic size={22} />Meeting workflow</div>
          <div className="pipeline">
            <div><AudioLines size={18} /> Transcript</div>
            <div><ClipboardList size={18} /> Summary</div>
            <div><ListTodo size={18} /> Actions</div>
            <div><ShieldCheck size={18} /> Review</div>
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <div className="section-copy"><h2>{t.featuresTitle}</h2><p>{t.featuresSubtitle}</p></div>
        <div className="feature-grid">
          {[FileAudio, BadgeCheck, ListTodo, ShieldCheck].map((Icon, index) => (
            <article key={t.features[index][0]}><Icon size={24} /><h3>{t.features[index][0]}</h3><p>{t.features[index][1]}</p></article>
          ))}
        </div>
      </section>

      <section className="desk-section" id="desk">
        <div className="desk">
          <div className="section-title"><Mic size={22} /><div><h2>{t.deskTitle}</h2><p>{t.deskSubtitle}</p></div></div>
          <label>{t.meetingType}<input value={meetingType} onChange={(event) => setMeetingType(event.target.value)} /></label>
          <label>{t.transcript}<textarea value={transcript} onChange={(event) => setTranscript(event.target.value)} /></label>
          <div className="composer-actions">
            <button onClick={() => setTranscript(samples[locale])}>{t.sample}</button>
            <button className="primary" onClick={generate} disabled={loading || !transcript.trim()}>{t.run}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="result-card">
          <h3>Minutes output</h3>
          <p>{result}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="governance" id="governance">
        <div><h2>{t.governanceTitle}</h2><p>{t.governanceSubtitle}</p></div>
        <div className="stats">
          <div><span>会议</span><strong>128</strong><em>本月</em></div>
          <div><span>行动项</span><strong>346</strong><em>已提取</em></div>
          <div><span>节省时间</span><strong>42h</strong><em>估算</em></div>
        </div>
        <div className="govern-grid">
          {t.panels.map(([title, content]) => <article key={title}><Users size={20} /><h3>{title}</h3><p>{content}</p></article>)}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
